// useImageModeration.js
// Uses nsfwjs (free, runs in browser, no API key) to detect explicit content
// before uploading to S3.
//
// SETUP (one time):
//   npm install nsfwjs @tensorflow/tfjs
//
// Usage:
//   const { checkImage, loading: moderating, error: modError } = useImageModeration();
//   const safe = await checkImage(file);  // returns true if safe, false if NSFW

import { useState, useRef } from 'react';

let nsfwModel = null; // singleton — load once

async function loadModel() {
  if (nsfwModel) return nsfwModel;
  try {
    // Dynamic import so it only loads when needed
    const tf    = await import('@tensorflow/tfjs');
    const nsfwjs = await import('nsfwjs');
    // Use the MobileNet model hosted on CDN
    nsfwModel = await nsfwjs.load('https://storage.googleapis.com/tfjs-models/tfjs/mobilenet_v2_1.0_224/model.json');
    return nsfwModel;
  } catch (e) {
    console.warn('NSFW model failed to load:', e);
    return null;
  }
}

export function useImageModeration() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const checkImage = async (file) => {
    setLoading(true);
    setError('');
    try {
      // Draw file to an img element for nsfwjs
      const url = URL.createObjectURL(file);
      const img  = new Image();
      img.src    = url;
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });

      const model = await loadModel();
      if (!model) {
        // If model fails to load, allow upload but log warning
        console.warn('Moderation model unavailable — skipping check');
        setLoading(false);
        return true;
      }

      const predictions = await model.classify(img);
      URL.revokeObjectURL(url);

      // nsfwjs categories: Drawing, Hentai, Neutral, Porn, Sexy
      const nsfw = predictions.find(p => ['Porn', 'Hentai'].includes(p.className));
      const sexy = predictions.find(p => p.className === 'Sexy');

      if (nsfw && nsfw.probability > 0.60) {
        setError('This image contains explicit content and cannot be uploaded. Please use a professional photo.');
        setLoading(false);
        return false;
      }
      if (sexy && sexy.probability > 0.80) {
        setError('This image may contain inappropriate content. Please use a professional photo.');
        setLoading(false);
        return false;
      }

      setLoading(false);
      return true;
    } catch (e) {
      console.warn('Moderation check error:', e);
      setLoading(false);
      return true; // fail open — don't block upload if check errors
    }
  };

  return { checkImage, loading, error };
}
